<template>
  <main role="main">
    <b-container>
      <div class="text-right">
        <b-dropdown text="Switch network" class="m-md-2" variant="link">
          <template #button-content>
            <img :alt="currentChainInfo.name" style="width: 20px; height: 20px;" :src="currentChainInfo.logo" class="mr-2">
            <span class="text-decoration-none">{{ currentChainInfo.name }}</span>
          </template>
          <b-dropdown-item v-for="switchChain in switchChains" :key="switchChain.chainId" :href="'/swap/' + switchChain.chainId">
            <img :alt="switchChain.name" style="width: 20px; height: 20px;" :src="switchChain.logo" class="mr-2">
            <span>{{ switchChain.name }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <b-row>
        <section class="px-3 col-lg-6 mb-5">
          <div class="border rounded mb-3 p-2">
            <div>
              <div class="label-row">
                <div>You sell</div>
                <div class="cursor-pointer text-secondary" @click="onClickAllIn">
                  <small>Balance: {{ swapInput.walletDisplayBalance }}</small>
                </div>
              </div>
              <div class="d-flex flex-row align-items-center justify-content-between">
                <div @click="onClickInputAsset" class="asset-btn">
                  <div class="flex-shrink-0 mr-2" style="width: 30px; height: 30px;">
                    <img v-if="swapInput.logo" :src="swapInput.logo" class="w-100 h-100">
                  </div>
                  <div class="overflow-hidden text-overflow-ellipsis">{{ swapInput.symbol }}</div>
                </div>
                <b-input type="text" v-model.trim="swapInput.displayAmount" @input="inputAmountChangedDebounced"
                         class="border-0 text-right flex-grow-1" size="lg"
                         autocomplete="off" inputmode="decimal"></b-input>
              </div>
              <div class="label-row small text-secondary">
                <div>
                  <a :href="getExplorerUrl(chainId, 'token', swapInput.contract)" target="_blank" class="mr-2 text-secondary">
                    {{ swapInput.name || swapInput.symbol }}
                  </a>
                  <template v-if="!swapInput.isNative">
                    <span id="btnCopyInputTokenAddress" class="cursor-pointer mr-2" @click="copyTokenAddress(swapInput.contract)">
                      <b-icon-files />
                    </span>
                    <b-tooltip target="btnCopyInputTokenAddress">
                      {{ swapInput.contract }}
                    </b-tooltip>
                  </template>
                </div>
              </div>
              <div class="label-row small text-secondary">
                <div>
                  <span class="mr-2 cursor-pointer"
                        @click="onClickSetWebTitlePriceType('input')"
                        :class="{ 'font-weight-500': webTitlePriceType === 'input' }">
                    1 {{ swapInput.symbol }} = {{ swapInput.displayPriceOther }} {{ swapOutput.symbol }}
                  </span>
                  <b-icon-info-circle id="input-unit-price-info"></b-icon-info-circle>
                  <b-tooltip target="input-unit-price-info">Quoted unit price when swapping with the specified volume</b-tooltip>
                </div>
              </div>
            </div>
            <div class="text-center">
              <span class="text-primary cursor-pointer" @click="reverseInputOutput" title="Reverse">
                <b-icon-arrow-down-up/>
              </span>
            </div>
            <div>
              <div class="label-row">
                <div>You buy</div>
                <div class="text-secondary"><small>Balance: {{ swapOutput.walletDisplayBalance }}</small></div>
              </div>
              <div class="d-flex flex-row align-items-center justify-content-between">
                <div @click="onClickOutputAsset" class="asset-btn">
                  <div class="flex-shrink-0 mr-2" style="width: 30px; height: 30px;">
                    <img v-if="swapOutput.logo" :src="swapOutput.logo" class="w-100 h-100">
                  </div>
                  <div class="overflow-hidden text-overflow-ellipsis">{{ swapOutput.symbol }}</div>
                </div>
                <div class="flex-grow-1 px-3 py-2 text-right overflow-hidden" style="font-size: 20px;"
                     :class="{'text-secondary': isRequestingQuote}">
                  {{ swapOutput.displayAmount }}
                </div>
              </div>
              <div class="label-row small text-secondary">
                <div>
                  <a :href="getExplorerUrl(chainId, 'token', swapOutput.contract)" target="_blank" class="mr-2 text-secondary">
                    {{ swapOutput.name || swapOutput.symbol }}
                  </a>
                  <template v-if="!swapOutput.isNative">
                    <span id="btnCopyOutputTokenAddress" class="cursor-pointer mr-2" @click="copyTokenAddress(swapOutput.contract)">
                      <b-icon-files />
                    </span>
                    <b-tooltip target="btnCopyOutputTokenAddress">
                      {{ swapOutput.contract }}
                    </b-tooltip>
                  </template>
                </div>
              </div>
              <div class="label-row small text-secondary">
                <div>
                  <span class="mr-2 cursor-pointer"
                        @click="onClickSetWebTitlePriceType('output')"
                        :class="{ 'font-weight-500': webTitlePriceType === 'output' }">
                    1 {{ swapOutput.symbol }} = {{ swapOutput.displayPriceOther }} {{ swapInput.symbol }}
                  </span>
                  <b-icon-info-circle id="output-unit-price-info"></b-icon-info-circle>
                  <b-tooltip target="output-unit-price-info">Quoted unit price when swapping with the specified volume</b-tooltip>
                </div>
              </div>
            </div>
          </div>
          <div v-if="quoteErrorMsg" class="mb-3 text-danger border border-danger d-flex flex-row align-items-center px-1 py-1">
            <div class="flex-grow-1 text-break">
              Error requesting quote: {{ quoteErrorMsg }}
            </div>
            <div class="cursor-pointer p-1" @click="quoteErrorMsg = ''"><b-icon-x-circle/></div>
          </div>
          <div class="d-flex flex-row flex-wrap align-items-center justify-content-between mb-2">
            <label class="m-0" for="inputSendToAddress">Send to address</label>
            <div class="text-primary">
              <span class="cursor-pointer mr-4" v-b-modal:address-book-modal><b-icon-bookmark/> Address book</span>
              <span class="cursor-pointer" v-b-modal:save-wallet-modal><b-icon-plus-lg/> Save new</span>
            </div>
          </div>
          <v-select class="vs-normalizer mb-3" :hidden="!!destReceiverLabel"
                    label="label"
                    :getOptionKey="s => s.address + ':' + s.memo"
                    :filter="destReceiverSelectDropdownFilter"
                    :options="savedWallets"
                    :clearSearchOnBlur="() => false"
                    v-on:option:selecting="onPickSavedWallet">
            <template v-slot:search="{attributes, events}">
              <input v-bind="attributes" v-on="events"
                     :value="destReceiver"
                     @input="destReceiver = $event.target.value"
                     @blur="destReceiver = destReceiver?.trim() || ''"
                     class="form-control text-monospace" required minlength="2" maxlength="100"/>
            </template>
            <template v-slot:option="{ label, address }">
              <div class="py-1">
                <div>{{ label }}</div>
                <div class="text-secondary text-monospace">
                  <span class="d-inline d-md-none">{{ getShortenedAddress(address) }}</span>
                  <span class="d-none d-md-inline">{{ address }}</span>
                </div>
              </div>
            </template>
          </v-select>
          <div v-if="destReceiverLabel" class="border rounded d-flex flex-row align-items-center mb-3 p-2">
            <div class="flex-grow-1 min-width-0">
              <div class="overflow-hidden text-overflow-ellipsis">{{ destReceiverLabel }}</div>
              <div class="text-monospace">
                <a class="text-break" :href="destReceiverExplorerUrl" target="_blank">{{ destReceiver }}</a>
              </div>
            </div>
            <div class="flex-shrink-0">
              <b-button variant="link" class="text-secondary" @click="clearDestReceiver"><b-icon-x-circle/></b-button>
            </div>
          </div>
          <div class="mb-4 text-right">
            <b-button variant="link mr-3" @click="refreshQuote" :disabled="isRequestingQuote">Refresh quote</b-button>
            <b-button variant="primary" style="width: 160px;" @click="doSwap" :disabled="isSwapping">Swap</b-button>
          </div>
          <div class="border rounded mb-3 p-2" v-if="reviewInfo">
            <h6 class="mb-3 pb-2 border-bottom">Review</h6>
            <table class="table table-borderless">
              <tbody>
              <tr>
                <td>Price: {{ reviewInfo.fromTokenSymbol }} / {{ reviewInfo.toTokenSymbol }}</td>
                <td class="text-right">{{ reviewInfo.displayPrice }}</td>
              </tr>
              <tr>
                <td>Price: {{ reviewInfo.toTokenSymbol }} / {{ reviewInfo.fromTokenSymbol }}</td>
                <td class="text-right">{{ reviewInfo.displayInversePrice }}</td>
              </tr>
              <tr>
                <td>You spend</td>
                <td class="text-right">{{ reviewInfo.displayInputAmount }} {{ reviewInfo.fromTokenSymbol }}</td>
              </tr>
              <tr>
                <td>Estimated received</td>
                <td class="text-right">{{ reviewInfo.displayOutputAmount }} {{ reviewInfo.toTokenSymbol }}</td>
              </tr>
              <tr>
                <td>Minimum received</td>
                <td class="text-right">{{ reviewInfo.displayMinimumReceiveAmount }} {{ reviewInfo.toTokenSymbol }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-if="swapErrorMsg" class="mb-3 text-danger border border-danger d-flex flex-row align-items-center px-1 py-1">
            <div class="flex-grow-1 text-break">
              <div class="text-break small" style="max-height: 100px; overflow-y: scroll;">
                Error requesting swap: {{ swapErrorMsg }}
              </div>
              <div v-if="showApproveInfinite">
                <b-btn variant="primary" size="sm" :disabled="isApproving" @click="onClickApprove">
                  Approve {{ swapInput.symbol }}
                </b-btn>
                <b-btn variant="secondary" size="sm" v-if="showSetApproveToZero" :disabled="isApproving" class="ml-3" @click="onClickRevoke">
                  Revoke {{ swapInput.symbol }}
                </b-btn>
              </div>
            </div>
            <div class="cursor-pointer p-1" @click="swapErrorMsg = ''"><b-icon-x-circle/></div>
          </div>
          <div v-if="sentTxExplorerUrl" class="mb-3 text-info border border-info d-flex flex-row align-items-center px-1 py-1">
            <div class="flex-grow-1 text-break">
              Transaction sent. <a :href="sentTxExplorerUrl" target="_blank">View on explorer</a>
            </div>
            <div class="cursor-pointer p-1" @click="sentTxExplorerUrl = ''"><b-icon-x-circle/></div>
          </div>
          <div class="border rounded mb-3 px-3 py-2">
            <h6 class="mb-3 pb-2 border-bottom">Swap options</h6>
            <div class="d-flex flex-row align-items-center justify-content-between mb-2">
              <label class="m-0">Aggregator</label>
              <b-form-radio-group v-model="aggregator" :options="aggregatorOptions"></b-form-radio-group>
            </div>
            <div class="mb-3">
              <div><label>Slippage tolerance</label></div>
              <div class="d-flex flex-row flex-wrap align-items-center">
                <div v-for="slippageOption in slippageToleranceOptions" :key="slippageOption"
                     class="flex-shrink-0 small text-center mr-2 mb-2 px-2 py-1 cursor-pointer border rounded-pill"
                     :class="{ 'bg-primary': slippageOption === slippageTolerance, 'text-white': slippageOption === slippageTolerance }"
                     style="width: 64px;" @click="slippageTolerance = slippageOption">
                  {{ slippageOption }}%
                </div>
                <b-input-group class="flex-grow-1 mb-2" style="min-width: 120px; flex-basis: 120px;">
                  <b-form-input type="number" v-model.number="slippageTolerance" class="text-right" @blur="onInputSlippageBlur"
                                min="0" step="0.001" max="50"></b-form-input>
                  <b-input-group-append>
                    <b-input-group-text>%</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <div v-if="slippageTolerance >= 10" class="text-danger text-right mt-2 small">Warning: high slippage</div>
            </div>
          </div>
        </section>

        <section class="px-3 col-lg-6">
          <div class="border rounded mb-3 px-3 py-2">
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div>
                <span v-if="!showBotOrderForm" class="text-primary cursor-pointer" @click="showBotOrderForm = true">Create bot order</span>
                <span v-if="showBotOrderForm" class="text-primary cursor-pointer" @click="showBotOrderForm = false">Hide form</span>
              </div>
              <div v-if="showBotOrderForm">
                <router-link to="/cex-balance-watch-orders">Manage orders</router-link>
              </div>
            </div>
            <div v-if="showBotOrderForm" class="border-top mt-2 pt-2">
              <NewCexBalanceWatchOrderModal
                ref="cex-balance-watch-form"
                :p_base-asset="swapOutput && swapOutput.symbol"
                :p_quote-asset="swapInput && swapInput.symbol"
              />
            </div>
          </div>
        </section>
      </b-row>
    </b-container>

    <b-modal id="pick-swap-token-modal" title="Select a token" hide-footer no-fade no-close-on-backdrop>
      <PickTokenModal :chainId="chainId" :walletAddress="walletAddress" @pickToken="onPickToken"></PickTokenModal>
    </b-modal>
    <b-modal id="address-book-modal" title="Address book" hide-footer no-fade no-close-on-backdrop @hidden="reloadSavedWallets">
      <AddressBookModal type="tron" @pickWallet="onPickSavedWallet"></AddressBookModal>
    </b-modal>
    <b-modal id="save-wallet-modal" title="Save new wallet" hide-footer no-fade no-close-on-backdrop @hidden="reloadSavedWallets">
      <SaveWalletModal modal-id="save-wallet-modal" :prefillAddress="destReceiver" type="tron" @done="onPickSavedWallet"></SaveWalletModal>
    </b-modal>
  </main>
</template>

<style lang="scss" scoped>
  .label-row {
    height: 1.375rem;
    padding: 0 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .asset-btn {
    flex-shrink: 0;
    max-width: 50%;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 16px;
    cursor: pointer;
    border-radius: 8px;
    margin-right: 16px;

    &:hover {
      background-color: #e8e8e8;
    }
  }

  table {
    tr, td {
      padding: 8px 16px;
    }
  }
</style>

<script lang="ts">
  import _ from "lodash";
  import BigNumber from "bignumber.js";
  import * as web3Service from "@/services/web3Service";
  import {ethers} from "ethers";
  import * as savedWalletsService from "@/services/savedWalletsService";
  import * as customTokensService from "@/services/customTokensService";
  import * as sunSwapRouterService from "@/services/sunSwapRouterService";
  import * as utils from "@/utils";
  import * as constants from "@/constants";
  import PickTokenModal from "@/components/PickTokenModal.vue";
  import SaveWalletModal from "@/components/SaveWalletModal.vue";
  import AddressBookModal from "@/components/AddressBookModal.vue";
  import NewCexBalanceWatchOrderModal from "@/components/NewCexBalanceWatchOrderModal.vue";
  import erc20Abi from "@/services/abi/erc20Abi.json";
  import sunSwapRouterAbi from "@/services/abi/sunSwapRouterAbi.json";
  import type {SwapRouterResponse} from "@/services/sunSwapRouterService";

  export default {
    name: "SwapTron",
    components: {
      NewCexBalanceWatchOrderModal, AddressBookModal, SaveWalletModal, PickTokenModal
    },
    inject: ["toastError", "toastSuccess", "toastSuccessDelay", "showLoading", "hideLoading", "getShortenedAddress", "getExplorerUrl"],

    data() {
      return {
        chainId: constants.CHAIN_ID_TRON,
        walletAddress: "",

        switchChains: [
          constants.CHAIN_ID_ETH,
          constants.CHAIN_ID_BSC,
          constants.CHAIN_ID_POLYGON,
          constants.CHAIN_ID_ARBITRUM,
          constants.CHAIN_ID_OPTIMISM,
          constants.CHAIN_ID_BASE,
          constants.CHAIN_ID_SONIC,
          constants.CHAIN_ID_SOLANA,
          constants.CHAIN_ID_TRON,
        ].map(chainId => ({
          chainId,
          name: constants.chainNames[chainId],
          logo: constants.NETWORK_LOGO[chainId]
        })),

        swapInput: {
          name: "",
          contract: "",
          isNative: false,
          decimals: 0,
          symbol: "",
          logo: "",
          walletBalance: "0",
          walletDisplayBalance: "0",
          displayAmount: "",
          displayPriceUsd: "--",
          displayPriceOther: "--",
        },

        swapOutput: {
          name: "",
          contract: "",
          isNative: false,
          decimals: 0,
          symbol: "",
          logo: "",
          walletBalance: "0",
          walletDisplayBalance: "0",
          displayAmount: "",
          displayPriceUsd: "--",
          displayPriceOther: "--",
        },

        webTitlePriceType: "input",

        destReceiverLabel: "",
        destReceiver: "",
        savedWallets: [],

        pickingTokenMode: "input" as "input" | "output",

        reviewInfo: null as {
          fromTokenSymbol: string,
          toTokenSymbol: string,
          displayInputAmount: string,
          displayOutputAmount: string,
          displayMinimumReceiveAmount: string,
          displayPrice: string,
          displayInversePrice: string,
        },

        isRequestingQuote: false,
        quoteErrorMsg: "",

        isSwapping: false,
        swapErrorMsg: "",

        showApproveInfinite: false,
        showSetApproveToZero: false,
        isApproving: false,

        slippageTolerance: 1,
        slippageToleranceOptions: [0.1, 0.5, 1, 3],

        aggregatorOptions: [
          { value: "sunio", text: "sun.io", disabled: false },
        ],
        aggregator: "sunio",

        routerData: null as SwapRouterResponse,

        sentTxExplorerUrl: "",

        showBotOrderForm: false,

        refreshSwapTokensAndBalancesTimeoutHandler: null,
        refreshQuoteTimeoutHandler: null,
        refreshWalletTokenBalancesTimeoutHandler: null,

        isDestroyed: false,
      };
    },

    computed: {
      currentChainInfo() {
        return { chainId: this.chainId, name: constants.chainNames[this.chainId], logo: constants.NETWORK_LOGO[this.chainId] };
      },
      destReceiverExplorerUrl() {
        return utils.getExplorerUrl(this.chainId, "address", this.destReceiver);
      }
    },

    created() {
      this.inputAmountChangedDebounced = _.debounce(this.inputAmountChanged, 300);
    },
    async mounted() {
      document.title = "Swap";
      this.walletAddress = await web3Service.getConnectedTronAddress();

      this.loadSettings();
      this.reloadSavedWallets();

      this.refreshWalletTokenBalances();
      await this.refreshSwapTokensAndBalances();
      this.refreshQuote();
    },

    methods: {
      loadSettings() {
        try {
          const savedObj = JSON.parse(localStorage.getItem(`swap-${this.chainId}`));
          Object.assign(this, _.pick(savedObj, [
            "aggregator", "slippageTolerance",
          ]));
          Object.assign(this.swapInput, _.pick(savedObj.swapInput, ["name", "contract", "symbol", "decimals", "displayAmount"]));
          Object.assign(this.swapOutput, _.pick(savedObj.swapOutput, ["name", "contract", "symbol", "decimals"]));
          // Object.assign(this.swapInput, _.pick(savedObj.swapInput, ["contract", "displayAmount"]));
          // Object.assign(this.swapOutput, _.pick(savedObj.swapOutput, ["contract"]));

          const destReceiverObj = customTokensService.getSwapDestReceiverByToken(this.chainId, this.swapOutput.contract);
          this.destReceiver = destReceiverObj?.destReceiver || "";
          this.destReceiverLabel = destReceiverObj?.destReceiverLabel || "";

        } catch (e) {
          console.error(e);
        }
      },

      reloadSavedWallets() {
        this.savedWallets = savedWalletsService.getAllSavedWallets2("tron");
      },

      async refreshSwapTokensAndBalances() {
        clearTimeout(this.refreshSwapTokensAndBalancesTimeoutHandler);
        if (this.isDestroyed) return;

        try {
          this.walletAddress = web3Service.getTronWeb().defaultAddress.base58;
          const oneInchSupportedTokensObj = await web3Service.getListedTokens(this.chainId);

          // input token metadata
          if (!this.swapInput.contract) {
            this.swapInput.contract = constants.ZERO_ADDRESS_TRON;
            this.swapInput.displayAmount = "1000";
          }
          this.swapInput.isNative = this.swapInput.contract === constants.ZERO_ADDRESS_TRON;

          const inputTokenDetailsFrom1inch = oneInchSupportedTokensObj[this.swapInput.contract];
          if (inputTokenDetailsFrom1inch) {
            this.swapInput.name = inputTokenDetailsFrom1inch.name;
            this.swapInput.decimals = inputTokenDetailsFrom1inch.decimals;
            this.swapInput.symbol = inputTokenDetailsFrom1inch.symbol;
            this.swapInput.logo = inputTokenDetailsFrom1inch.logoURI;
          } else if (!this.swapInput.name || !utils.isTokenDecimalsValid(this.swapInput.decimals) || !this.swapInput.symbol) {
            const inputTokenDetailsFromProvider = await web3Service.getTronTokenMetadata(this.swapInput.contract);
            this.swapInput.name = inputTokenDetailsFromProvider.name;
            this.swapInput.decimals = inputTokenDetailsFromProvider.decimals;
            this.swapInput.symbol = inputTokenDetailsFromProvider.symbol;
          }

          // output token metadata
          if (!this.swapOutput.contract) {
            this.swapOutput.contract = constants.USDT_TOKEN[this.chainId].address;
          }
          this.swapOutput.isNative = this.swapOutput.contract === constants.ZERO_ADDRESS_TRON;

          const outputTokenDetailsFrom1inch = oneInchSupportedTokensObj[this.swapOutput.contract];
          if (outputTokenDetailsFrom1inch) {
            this.swapOutput.name = outputTokenDetailsFrom1inch.name;
            this.swapOutput.decimals = outputTokenDetailsFrom1inch.decimals;
            this.swapOutput.symbol = outputTokenDetailsFrom1inch.symbol;
            this.swapOutput.logo = outputTokenDetailsFrom1inch.logoURI;
          } else if (!this.swapOutput.name || !utils.isTokenDecimalsValid(this.swapOutput.decimals) || !this.swapOutput.symbol) {
            const outputTokenDetailsFromProvider = await web3Service.getTronTokenMetadata(this.swapOutput.contract);
            this.swapOutput.name = outputTokenDetailsFromProvider.name;
            this.swapOutput.decimals = outputTokenDetailsFromProvider.decimals;
            this.swapOutput.symbol = outputTokenDetailsFromProvider.symbol;
          }

          // input and output token balances
          const balances = await web3Service.getTronTokenBalances(this.walletAddress, [this.swapInput.contract, this.swapOutput.contract]);

          this.swapInput.walletBalance = (balances[this.swapInput.contract] || 0).toString();
          this.swapInput.walletDisplayBalance = BigNumber(this.swapInput.walletBalance)
            .shiftedBy(-this.swapInput.decimals)
            .decimalPlaces(8, BigNumber.ROUND_DOWN)
            .toFixed();

          this.swapOutput.walletBalance = (balances[this.swapOutput.contract] || 0).toString();
          this.swapOutput.walletDisplayBalance = BigNumber(this.swapOutput.walletBalance)
            .shiftedBy(-this.swapOutput.decimals)
            .decimalPlaces(8, BigNumber.ROUND_DOWN)
            .toFixed();

        } catch (e) {
          console.error(e);
        }

        clearTimeout(this.refreshSwapTokensAndBalancesTimeoutHandler);
        this.refreshSwapTokensAndBalancesTimeoutHandler = setTimeout(() => this.refreshSwapTokensAndBalances(), 3500);
      },

      async refreshWalletTokenBalances() {
        clearTimeout(this.refreshWalletTokenBalancesTimeoutHandler);
        if (this.isDestroyed) return;

        try {
          await Promise.allSettled([
            web3Service.getTronWalletTokenBalances(this.walletAddress),
            web3Service.getListedTokens(this.chainId)
          ]);
        } catch (e) {

        }

        clearTimeout(this.refreshWalletTokenBalancesTimeoutHandler);
        this.refreshWalletTokenBalancesTimeoutHandler = setTimeout(() => this.refreshWalletTokenBalances(), 10000);
      },


      onClickInputAsset() {
        this.pickingTokenMode = "input";
        this.$bvModal.show("pick-swap-token-modal");
      },

      onClickOutputAsset() {
        this.pickingTokenMode = "output";
        this.$bvModal.show("pick-swap-token-modal");
      },

      onPickToken(tokenObj: any) {
        if (this.pickingTokenMode === "input") {
          if (tokenObj.address === this.swapOutput.contract) {
            this.reverseInputOutput();
          } else {
            this.swapInput.name = tokenObj.name;
            this.swapInput.contract = tokenObj.address;
            this.swapInput.isNative = tokenObj.isNative;
            this.swapInput.decimals = tokenObj.decimals;
            this.swapInput.symbol = tokenObj.symbol;
            this.swapInput.logo = tokenObj.logo;
            this.swapInput.walletBalance = tokenObj.balance;
            this.swapInput.walletDisplayBalance = tokenObj.displayBalance;
            this.showApproveInfinite = false;
          }

        } else if (this.pickingTokenMode === "output") {
          if (tokenObj.address === this.swapInput.contract) {
            this.reverseInputOutput();
          } else {
            this.swapOutput.name = tokenObj.name;
            this.swapOutput.contract = tokenObj.address;
            this.swapOutput.isNative = tokenObj.isNative;
            this.swapOutput.decimals = tokenObj.decimals;
            this.swapOutput.symbol = tokenObj.symbol;
            this.swapOutput.logo = tokenObj.logo;
            this.swapOutput.walletBalance = tokenObj.balance;
            this.swapOutput.walletDisplayBalance = tokenObj.displayBalance;

            const destReceiverObj = customTokensService.getSwapDestReceiverByToken(this.chainId, this.swapOutput.contract);
            this.destReceiver = destReceiverObj?.destReceiver || "";
            this.destReceiverLabel = destReceiverObj?.destReceiverLabel || "";
          }
        }

        this.swapInput.displayPriceUsd = "--";
        this.swapInput.displayPriceOther = "--";
        this.swapOutput.displayPriceUsd = "--";
        this.swapOutput.displayPriceOther = "--";

        this.$bvModal.hide("pick-swap-token-modal");
        this.refreshQuote();
        this.refreshSwapTokensAndBalances();
      },

      onPickSavedWallet(savedWallet: savedWalletsService.SavedWallet) {
        this.destReceiver = savedWallet.address;
        this.destReceiverLabel = savedWallet.label;
        this.$bvModal.hide("address-book-modal");
      },

      clearDestReceiver() {
        this.destReceiverLabel = "";
        this.destReceiver = "";
      },

      destReceiverSelectDropdownFilter(options: any[], search: string) {
        search = utils.sanitizeSearchText(search).toLowerCase();
        return options.filter(savedWallet =>
          savedWallet.label.toLowerCase().includes(search) ||
          savedWallet.address.toLowerCase().includes(search)
        );
      },

      onClickAllIn() {
        this.swapInput.displayAmount = BigNumber(this.swapInput.walletBalance)
          .shiftedBy(-this.swapInput.decimals)
          .decimalPlaces(8, BigNumber.ROUND_DOWN)
          .toFixed();
        this.refreshQuote();
      },

      inputAmountChanged() {
        this.refreshQuote();
      },

      reverseInputOutput() {
        const temp = this.swapInput;
        this.swapInput = this.swapOutput;
        this.swapOutput = temp;

        this.swapInput.displayPriceUsd = "--";
        this.swapInput.displayPriceOther = "--";
        this.swapOutput.displayPriceUsd = "--";
        this.swapOutput.displayPriceOther = "--";

        const destReceiverObj = customTokensService.getSwapDestReceiverByToken(this.chainId, this.swapOutput.contract);
        this.destReceiver = destReceiverObj?.destReceiver || "";
        this.destReceiverLabel = destReceiverObj?.destReceiverLabel || "";

        this.showApproveInfinite = false;
        this.refreshQuote();
      },

      onClickSetWebTitlePriceType(type: string) {
        if (this.webTitlePriceType !== type) {
          this.webTitlePriceType = type;
          this.refreshQuote();
        }
      },

      onInputSlippageBlur() {
        if (!this.slippageTolerance) {
          this.slippageTolerance = 1;
        } else if (this.slippageTolerance > 40) {
          this.slippageTolerance = 40;
        } else if (this.slippageTolerance < 0.001) {
          this.slippageTolerance = 0.001;
        }
      },

      async refreshQuote() {
        clearTimeout(this.refreshQuoteTimeoutHandler);
        if (this.isDestroyed) return;

        const displayInputAmount = this.swapInput.displayAmount;
        const fromRealTokenAmountBN = BigNumber(displayInputAmount);
        if (!fromRealTokenAmountBN.gt(0)) {
          this.swapOutput.displayAmount = "";
          this.swapInput.displayPriceUsd = "--";
          this.swapInput.displayPriceOther = "--";
          this.swapOutput.displayPriceUsd = "--";
          this.swapOutput.displayPriceOther = "--";
          return;
        }

        try {
          this.isRequestingQuote = true;
          this.quoteErrorMsg = "";

          let inputPriceBN: BigNumber, outputPriceBN: BigNumber;

          if (this.aggregator === "sunio") {
            const quoteRes = await sunSwapRouterService.getSwapRouter({
              fromToken: this.swapInput.contract,
              fromTokenDecimals: this.swapInput.decimals,
              toToken: this.swapOutput.contract,
              toTokenDecimals: this.swapOutput.decimals,
              amountIn: fromRealTokenAmountBN
                .shiftedBy(this.swapInput.decimals)
                .decimalPlaces(0, BigNumber.ROUND_DOWN)
                .toFixed(),
              receiver: web3Service.getTronWeb().isAddress(this.destReceiver) ? this.destReceiver : this.walletAddress,
              slippage: this.slippageTolerance,
            });
            if ( // make sure params haven't changed on ui
              displayInputAmount === this.swapInput.displayAmount &&
              quoteRes.fromToken === this.swapInput.contract &&
              quoteRes.toToken === this.swapOutput.contract
            ) {
              const toRealTokenAmountBN = BigNumber(quoteRes.amountOut).shiftedBy(-quoteRes.toTokenDecimals);
              inputPriceBN = toRealTokenAmountBN.dividedBy(fromRealTokenAmountBN);
              this.swapInput.displayPriceOther = inputPriceBN.precision(6).toFixed();
              outputPriceBN = fromRealTokenAmountBN.dividedBy(toRealTokenAmountBN);
              this.swapOutput.displayPriceOther = outputPriceBN.precision(6).toFixed();
              this.swapOutput.displayAmount = toRealTokenAmountBN.decimalPlaces(8).toFixed();
              this.routerData = quoteRes;
            }
          }

          if (!this.isDestroyed && inputPriceBN && outputPriceBN) {
            if (this.webTitlePriceType === "input") {
              document.title = `${inputPriceBN.precision(6).toString()} ${this.swapInput.symbol}/${this.swapOutput.symbol} | Swap`;
            } else {
              document.title = `${outputPriceBN.precision(6).toString()} ${this.swapOutput.symbol}/${this.swapInput.symbol} | Swap`;
            }
          }

        } catch (e) {
          this.quoteErrorMsg = e.response?.data?.description || e.response?.data?.error || e.message;
          console.error(e);

        } finally {
          this.isRequestingQuote = false;
        }

        this.saveSettings();

        clearTimeout(this.refreshQuoteTimeoutHandler);
        this.refreshQuoteTimeoutHandler = setTimeout(() => this.refreshQuote(), 5000);
      },

      async doSwap() {
        if (this.destReceiver && !web3Service.getTronWeb().isAddress(this.destReceiver)) {
          this.swapErrorMsg = "Invalid receiver address";
          return;
        }

        try {
          this.isSwapping = true;
          this.swapErrorMsg = "";
          this.showApproveInfinite = false;
          this.showSetApproveToZero = false;
          this.sentTxExplorerUrl = "";

          if (!this.walletAddress) {
            throw new Error("Wallet not connected");
          }

          let fromTokenAmountBN = BigNumber(this.swapInput.displayAmount)
            .shiftedBy(this.swapInput.decimals)
            .decimalPlaces(0, BigNumber.ROUND_DOWN);

          if (!fromTokenAmountBN.gt(0)) {
            throw new Error("Zero input amount");
          }

          const inputWalletBalanceBN = BigNumber(this.swapInput.walletBalance);
          if (inputWalletBalanceBN.gt(0)) {
            const divByBalance = fromTokenAmountBN.div(inputWalletBalanceBN);
            if (divByBalance.gt(1) && divByBalance.lt(1.0001)) {
              fromTokenAmountBN = inputWalletBalanceBN;
            }
          }

          const fromTokenAmount = fromTokenAmountBN.toFixed();
          const slippage = this.slippageTolerance || 1;

          try {
            customTokensService.setSwapDestReceiverByToken(this.chainId, this.swapOutput.contract, this.destReceiver);
          } catch (e) {
            console.error(e);
          }

          if (this.aggregator === "sunio") {
            const checkRet = await sunSwapRouterService.checkBalanceAndAllowance(this.walletAddress, this.swapInput.contract, fromTokenAmount);
            console.log(checkRet);
            const routerData = utils.jsonClone(this.routerData);

            if (
              routerData.fromToken === this.swapInput.contract &&
              routerData.toToken === this.swapOutput.contract &&
              routerData.amountIn === fromTokenAmount
            ) {
              const fromRealTokenAmountBN = BigNumber(routerData.amountIn).shiftedBy(-routerData.fromTokenDecimals);
              const toRealTokenAmountBN = BigNumber(routerData.amountOut).shiftedBy(-routerData.toTokenDecimals);

              this.reviewInfo = {
                displayPrice: toRealTokenAmountBN.dividedBy(fromRealTokenAmountBN).precision(6).toFixed(),
                displayInversePrice: fromRealTokenAmountBN.dividedBy(toRealTokenAmountBN).precision(6).toFixed(),
                displayInputAmount: fromRealTokenAmountBN.decimalPlaces(8).toFixed(),
                displayOutputAmount: toRealTokenAmountBN.decimalPlaces(8).toFixed(),
                displayMinimumReceiveAmount: toRealTokenAmountBN.multipliedBy(1 - slippage / 100).decimalPlaces(8).toFixed(),
                fromTokenSymbol: this.swapInput.symbol,
                toTokenSymbol: this.swapOutput.symbol,
              };

              const funAbi = sunSwapRouterAbi.find(it => it.name === "swapExactInput");
              const rawParameter = web3Service.getTronWeb().utils.abi.encodeParamsV2ByABI(funAbi, routerData.smartRouterParams);
              console.log("rawParameter", rawParameter);

              const unsignedTx = await web3Service.getTronWeb().transactionBuilder.triggerSmartContract(
                constants.SUNSWAP_ROUTER,
                "swapExactInput(address[],string[],uint256[],uint24[],(uint256,uint256,address,uint256))",
                {
                  callValue: routerData.fromToken === constants.ZERO_ADDRESS_TRON ? fromTokenAmount : 0,
                  feeLimit: 500e6,
                  rawParameter
                }
              );
              /*
              {
                  "result": {
                      "result": true
                  },
                  "transaction": {
                      "visible": false,
                      "txID": "6fe8630e77db6c3a2e730430843b058a9b67c6f766d13f077802b86b44568743",
                      "raw_data": {
                          "contract": [
                              {
                                  "parameter": {
                                      "value": {
                                          "data": "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",
                                          "owner_address": "4118ddb4a3eb113aa710927f2caa2881bd185ac05b",
                                          "contract_address": "413c9e0ac33f138216c50638d71c344a299d0d1030",
                                          "call_value": 30000000
                                      },
                                      "type_url": "type.googleapis.com/protocol.TriggerSmartContract"
                                  },
                                  "type": "TriggerSmartContract"
                              }
                          ],
                          "ref_block_bytes": "6845",
                          "ref_block_hash": "4a50dca18d5fca4d",
                          "expiration": 1708055649000,
                          "fee_limit": 500000000,
                          "timestamp": 1708055591759
                      },
                      "raw_data_hex": "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"
                  }
              }
               */
              console.log("unsignedTx", unsignedTx);
              const signedTx = await web3Service.getTronWeb().trx.sign(unsignedTx.transaction);

              const tx = await web3Service.getTronWeb().trx.sendRawTransaction(signedTx);
              /*
              {
                  "result": true,
                  "txid": "6fe8630e77db6c3a2e730430843b058a9b67c6f766d13f077802b86b44568743",
                  "transaction": {
                      "visible": false,
                      "txID": "6fe8630e77db6c3a2e730430843b058a9b67c6f766d13f077802b86b44568743",
                      "raw_data": {
                          "contract": [
                              {
                                  "parameter": {
                                      "value": {
                                          "data": "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",
                                          "owner_address": "4118ddb4a3eb113aa710927f2caa2881bd185ac05b",
                                          "contract_address": "413c9e0ac33f138216c50638d71c344a299d0d1030",
                                          "call_value": 30000000
                                      },
                                      "type_url": "type.googleapis.com/protocol.TriggerSmartContract"
                                  },
                                  "type": "TriggerSmartContract"
                              }
                          ],
                          "ref_block_bytes": "6845",
                          "ref_block_hash": "4a50dca18d5fca4d",
                          "expiration": 1708055649000,
                          "fee_limit": 500000000,
                          "timestamp": 1708055591759
                      },
                      "raw_data_hex": "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",
                      "signature": [
                          "aacc6ebfa2df377b758199642939d786eb808834d9006727da511e8b1d2f666c4ece987435d85cec0ee570244722636f115adcfefd2294c9cd7996be5f2f5b6a1C"
                      ]
                  }
              }
               */
              this.sentTxExplorerUrl = utils.getExplorerUrl(this.chainId, "tx", tx.transaction.txID);
              console.log(tx);
            }

          }

        } catch (e) {
          console.error(e);
          this.swapErrorMsg = e.response?.data?.description || e.response?.data?.error || e.message;
          if (this.swapErrorMsg?.toLowerCase().includes("not enough allowance")) {
            this.showApproveInfinite = true;
            this.showSetApproveToZero = true;
          }

        } finally {
          this.isSwapping = false;
          this.reviewInfo = null;
        }
      },

      async onClickApprove() {
        try {
          this.isApproving = true;

          if (this.aggregator === "sunio") {
            const funAbi = erc20Abi.find(it => it.name === "approve");
            const rawParameter = web3Service.getTronWeb().utils.abi.encodeParamsV2ByABI(funAbi, [constants.SUNSWAP_ROUTER, ethers.MaxUint256]);
            const unsignedTx = await web3Service.getTronWeb().transactionBuilder.triggerSmartContract(
              this.swapInput.contract,
              "approve(address,uint256)",
              {
                callValue: 0,
                rawParameter
              }
            );
            const signedTx = await web3Service.getTronWeb().trx.sign(unsignedTx.transaction);
            await web3Service.getTronWeb().trx.sendRawTransaction(signedTx);
          }

          this.swapErrorMsg = "";
          this.showApproveInfinite = false;
          this.showSetApproveToZero = false;

        } catch (e) {
          console.error(e);

        } finally {
          this.isApproving = false;
        }
      },
      async onClickRevoke() {
        try {
          this.isApproving = true;

          if (this.aggregator === "sunio") {
            const funAbi = erc20Abi.find(it => it.name === "approve");
            const rawParameter = web3Service.getTronWeb().utils.abi.encodeParamsV2ByABI(funAbi, [constants.SUNSWAP_ROUTER, 0]);
            const unsignedTx = await web3Service.getTronWeb().transactionBuilder.triggerSmartContract(
              this.swapInput.contract,
              "approve(address,uint256)",
              {
                callValue: 0,
                rawParameter
              }
            );
            const signedTx = await web3Service.getTronWeb().trx.sign(unsignedTx.transaction);
            await web3Service.getTronWeb().trx.sendRawTransaction(signedTx);
          }

          this.swapErrorMsg = "";
          this.showApproveInfinite = false;
          this.showSetApproveToZero = false;

        } catch (e) {

        } finally {
          this.isApproving = false;
        }
      },


      saveSettings() {
        const savedObj = _.pick<any>(this, [
          "aggregator", "slippageTolerance",
        ]);
        savedObj.swapInput = _.pick(this.swapInput, ["name", "contract", "symbol", "decimals", "displayAmount"]);
        savedObj.swapOutput = _.pick(this.swapOutput, ["name", "contract", "symbol", "decimals"]);
        localStorage.setItem(`swap-${this.chainId}`, JSON.stringify(savedObj));
        localStorage.setItem(`swap`, JSON.stringify({ chainId: this.chainId }));
      }
    },

    unmounted() {
      clearTimeout(this.refreshSwapTokensAndBalancesTimeoutHandler);
      clearTimeout(this.refreshQuoteTimeoutHandler);
      clearTimeout(this.refreshWalletTokenBalancesTimeoutHandler);
      this.isDestroyed = true;
    }
  };

</script>
